import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axiosInstance from "../utils/axios";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { usePageSummary } from "../hooks/usePage";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import PublicPageShell from "../components/PublicPageShell";

// Skeleton Loader for board members
const BoardSkeleton = () => (
  <div className="flex flex-col items-center text-center bg-white p-4 rounded-lg shadow-md w-full animate-pulse">
    <div className="w-32 h-32 rounded-full bg-gray-200 mb-4" />
    <div className="h-4 w-2/3 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-1/2 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-3/4 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-1/3 bg-gray-200 rounded" />
  </div>
);

const Team = () => {
  const { pageHeader, isLoading } = usePageHeaderByKey("board-members");
  const { boardSummary, boardLoading } = usePageSummary();
  const [boardMembers, setBoardMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance
      .get("/api/board-members")
      .then((response) => setBoardMembers(response.data))
      .catch((error) => console.error("Error fetching board members:", error))
      .finally(() => setLoading(false));
  }, []);

  const openDialog = (member) => {
    setSelectedMember(member);
    setVisible(true);
  };

  return (
    <PublicPageShell className="flex items-center justify-center text-red-900 lg:my-20 my-10">
      <div className="flex flex-col justify-center items-center mx-auto w-full max-w-6xl">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="lg:text-3xl text-2xl font-black mb-8 text-center"
        >
          {pageHeader?.h1 || "Board Members"}
        </motion.h1>

        {/* Members Grid */}
        <motion.div
          className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 w-full"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.2 },
            },
          }}
        >
          {loading || isLoading
            ? Array.from({ length: 6 }).map((_, i) => <BoardSkeleton key={i} />)
            : boardMembers.map((member) => (
                <motion.div
                  key={member.id}
                  className="flex flex-col items-center text-center bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow w-full cursor-pointer"
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                  }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => openDialog(member)}
                >
                  {/* Profile Image */}
                  <motion.img
                    src={member.imageUrl}
                    alt={member.fullName}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md transition-transform duration-200"
                    whileHover={{ scale: 1.1, rotate: 2 }}
                  />

                  {/* Member Details */}
                  <p className="font-bold mt-4 text-base sm:text-lg">{member.fullName}</p>
                  <p className="text-gray-500 text-sm">{member.company}</p>
                  <p className="text-xs text-red-800 font-semibold mt-2 break-all">{member.email}</p>
                  <p className="text-xs text-gray-700">{member.phone}</p>
                </motion.div>
              ))}
        </motion.div>

        {/* Board Summary */}
        {boardSummary && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="lg:w-2/4 w-full leading-relaxed bg-white mt-8 p-4 border-s-4 border-red-900 text-center"
          >
            <span dangerouslySetInnerHTML={{ __html: boardSummary.description }} />
          </motion.p>
        )}

        {/* Member Dialog */}
        <Dialog
          header={selectedMember ? selectedMember.fullName : ""}
          visible={visible}
          className="w-full lg:w-[50%]"
          onHide={() => setVisible(false)}
        >
          {selectedMember && (
            <div className="p-4">
              <img
                src={selectedMember.imageUrl}
                alt={selectedMember.fullName}
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md mx-auto"
              />
              <p className="text-lg font-semibold text-center mt-3">{selectedMember.company}</p>
              <p className="text-center text-sm text-gray-500">{selectedMember.email}</p>
              <p className="text-center text-sm text-gray-500">{selectedMember.phone}</p>
              <p className="mt-4 text-gray-800 leading-relaxed">{selectedMember.bio}</p>
            </div>
          )}
          <div className="flex justify-end">
            <Button label="Close" className="p-button-danger" onClick={() => setVisible(false)} />
          </div>
        </Dialog>
      </div>
    </PublicPageShell>
  );
};

export default Team;
